import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../auth'
import { api, type PlatformClinic } from '../../api'
import { Icon, P, initials } from './ui'
import Dashboard from './Dashboard'
import Clinics from './Clinics'
import Activity from './Activity'
import Backups from './Backups'
import '../../platform.css'

/* The platform admin: the people who run this deployment. Its own shell, in
 * the same layout language as TradeDesk - sidebar, sticky top bar, page
 * header, cards and tables - with an accent theme and dark mode. */

interface PlatformState {
  clinics: PlatformClinic[] | null
  reload: () => Promise<void>
}
const Ctx = createContext<PlatformState | null>(null)
export function usePlatform(): PlatformState {
  const v = useContext(Ctx)
  if (!v) throw new Error('usePlatform outside the platform layout')
  return v
}

const THEMES: { id: string; label: string; hex: string }[] = [
  { id: 'indigo', label: 'Indigo', hex: '#6246ea' },
  { id: 'emerald', label: 'Emerald', hex: '#0d9668' },
  { id: 'saffron', label: 'Saffron', hex: '#e2730a' },
  { id: 'rose', label: 'Rose', hex: '#e02e5c' },
  { id: 'ocean', label: 'Ocean', hex: '#0a83c2' },
  { id: 'graphite', label: 'Graphite', hex: '#424a5e' },
]

function stored(key: string, fallback: string): string {
  try { return localStorage.getItem(key) ?? fallback } catch { return fallback }
}
function store(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* a preference, not data */ }
}

export default function PlatformLayout() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const [clinics, setClinics] = useState<PlatformClinic[] | null>(null)
  const [theme, setTheme] = useState(() => stored('pa.theme', 'indigo'))
  const [mode, setMode] = useState(() => stored('pa.mode', window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'))
  const [collapsed, setCollapsed] = useState(() => stored('pa.collapsed', '0') === '1')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [menu, setMenu] = useState<null | 'theme' | 'account'>(null)
  const [q, setQ] = useState(params.get('q') ?? '')
  const searchRef = useRef<HTMLInputElement>(null)

  const reload = useCallback(async () => {
    const r = await api.get<{ clinics: PlatformClinic[] }>('/api/platform/clinics')
    setClinics(r.clinics)
  }, [])
  useEffect(() => { void reload().catch(() => setClinics([])) }, [reload])

  useEffect(() => { setMobileOpen(false); setMenu(null) }, [pathname])
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); searchRef.current?.focus() }
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [])
  useEffect(() => {
    if (!menu) return
    const close = () => setMenu(null)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [menu])

  function search(e: React.FormEvent) {
    e.preventDefault()
    navigate(`/platform/clinics${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`)
  }

  const suspended = clinics?.filter((c) => c.status === 'suspended').length ?? 0
  const nav: { group: string; items: { to: string; label: string; icon: string; end?: boolean; count?: number }[] }[] = [
    { group: 'Overview', items: [{ to: '/platform', label: 'Dashboard', icon: P.grid, end: true }] },
    { group: 'Tenants', items: [{ to: '/platform/clinics', label: 'Clinics', icon: P.building, count: clinics?.length }] },
    { group: 'Operations', items: [
      { to: '/platform/activity', label: 'Activity log', icon: P.list },
      { to: '/platform/backups', label: 'Backups', icon: P.db },
    ] },
  ]

  return (
    <Ctx.Provider value={{ clinics, reload }}>
      <div className="pa" data-theme={theme} data-mode={mode}>
        <div className="pa-shell">
          {mobileOpen && <div className="pa-backdrop pa-only-sm" style={{ zIndex: 65 }} onClick={() => setMobileOpen(false)} />}
          <aside className={`pa-side${collapsed ? ' collapsed' : ''}${mobileOpen ? ' open' : ''}`} aria-label="Platform navigation">
            <div className="pa-brand">
              <span className="pa-logo"><Icon d={P.heart} size={17} /></span>
              <div><b>ClinicDesk</b><small>Platform admin</small></div>
            </div>
            <nav className="pa-nav">
              {nav.map((g) => (
                <div key={g.group}>
                  <div className="pa-group-label">{g.group}</div>
                  {g.items.map((it) => (
                    <NavLink key={it.to} to={it.to} end={it.end} title={it.label}
                      className={({ isActive }) => `pa-item${isActive ? ' active' : ''}`}>
                      <Icon d={it.icon} size={17} />
                      <span>{it.label}</span>
                      {it.count != null && <em className="count" style={{ fontStyle: 'normal' }}>{it.count}</em>}
                    </NavLink>
                  ))}
                </div>
              ))}
              {suspended > 0 && (
                <div className="pa-item" style={{ cursor: 'default' }} title={`${suspended} suspended`}>
                  <Icon d={P.pause} size={17} /><span>{suspended} suspended</span>
                </div>
              )}
            </nav>
            <div className="pa-side-foot">
              <button type="button" className="pa-item pa-hide-sm" style={{ width: '100%', border: 0, background: 'none', cursor: 'pointer' }}
                onClick={() => { const v = !collapsed; setCollapsed(v); store('pa.collapsed', v ? '1' : '0') }}>
                <Icon d={P.collapse} size={17} /><span>{collapsed ? 'Expand' : 'Collapse'}</span>
              </button>
            </div>
          </aside>

          <div className="pa-main">
            <header className="pa-top">
              <button type="button" className="pa-btn pa-icon-btn pa-only-sm" aria-label="Open menu" onClick={() => setMobileOpen(true)}><Icon d={P.menu} size={18} /></button>
              <form className="pa-search" onSubmit={search} role="search">
                <Icon d={P.search} size={15} />
                <input ref={searchRef} aria-label="Search clinics" placeholder="Search clinics by name or handle" value={q} onChange={(e) => setQ(e.target.value)} />
                <span className="pa-kbd pa-hide-sm">⌘K</span>
              </form>
              <div className="pa-top-right">
                <div className="pa-menu-wrap">
                  <button type="button" className="pa-btn pa-icon-btn" aria-label="Theme colour" aria-expanded={menu === 'theme'}
                    onClick={(e) => { e.stopPropagation(); setMenu(menu === 'theme' ? null : 'theme') }}><Icon d={P.palette} size={18} /></button>
                  {menu === 'theme' && (
                    <div className="pa-menu" onClick={(e) => e.stopPropagation()}>
                      <div className="pa-menu-label">Accent</div>
                      <div className="pa-swatches">
                        {THEMES.map((t) => (
                          <button key={t.id} type="button" aria-label={t.label} title={t.label} className={theme === t.id ? 'on' : ''}
                            style={{ background: t.hex }} onClick={() => { setTheme(t.id); store('pa.theme', t.id) }} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button type="button" className="pa-btn pa-icon-btn" aria-label={mode === 'dark' ? 'Light mode' : 'Dark mode'}
                  onClick={() => { const m = mode === 'dark' ? 'light' : 'dark'; setMode(m); store('pa.mode', m) }}>
                  <Icon d={mode === 'dark' ? P.sun : P.moon} size={18} />
                </button>
                <div className="pa-menu-wrap">
                  <button type="button" className="pa-btn pa-btn-ghost" style={{ padding: '0 6px', gap: 8 }} aria-expanded={menu === 'account'}
                    onClick={(e) => { e.stopPropagation(); setMenu(menu === 'account' ? null : 'account') }}>
                    <span className="pa-avatar t-primary" style={{ width: 30, height: 30 }}>{initials(user?.fullName ?? '?')}</span>
                    <span className="pa-hide-sm" style={{ fontWeight: 600, fontSize: 13 }}>{user?.fullName}</span>
                  </button>
                  {menu === 'account' && (
                    <div className="pa-menu" onClick={(e) => e.stopPropagation()}>
                      <div style={{ padding: '8px 10px' }}>
                        <div style={{ fontWeight: 600 }}>{user?.fullName}</div>
                        <div className="subtle" style={{ fontSize: 12 }}>@{user?.username} · Platform admin</div>
                      </div>
                      <hr />
                      <a href="/change-password"><Icon d={P.key} />Change password</a>
                      <button type="button" onClick={() => void signOut().then(() => navigate('/login', { replace: true }))}><Icon d={P.logout} />Sign out</button>
                    </div>
                  )}
                </div>
              </div>
            </header>

            <main className="pa-content">
              <Routes>
                <Route index element={<Dashboard />} />
                <Route path="clinics" element={<Clinics />} />
                <Route path="activity" element={<Activity />} />
                <Route path="backups" element={<Backups />} />
                <Route path="*" element={<Navigate to="/platform" replace />} />
              </Routes>
            </main>
          </div>
        </div>
      </div>
    </Ctx.Provider>
  )
}
